import { JSONContent } from "@tiptap/core";
import { Node, NodeType, ResolvedPos } from "@tiptap/pm/model";

export const errorMessage = (error: any) => {
  // Get the message from the unkown error
  let message = "";
  if (typeof error === "string") {
    message = error.toUpperCase();
  } else if (error instanceof Error) {
    message = error.message;
  }
  return message;
};

export const errorRequiresRecreate = (error: any) => {
  const message = errorMessage(error);

  // We're getting permission or CACAO errors so recreate the Node
  if (message.includes("invalid_jws") || message.includes("expired")) {
    return true;
  } else {
    throw error;
  }
};

export const deDupeContent = (contentToDeDupe: any, by = "id") => {
  const contentByLateralId: { [k: string]: any } = {};
  // @ts-ignore
  contentToDeDupe.forEach((content) => {
    if (!content[by]) return;
    if (!(content[by] in contentByLateralId)) contentByLateralId[content[by]] = [];
    contentByLateralId[content[by]].push(content);
  });
  let deDupedContent = [];
  for (const [_lateralID, items] of Object.entries(contentByLateralId)) {
    // @ts-ignore
    const newest = items.reduce((a, b) => (a.created > b.created ? a : b));
    if (newest.deleted) continue;
    deDupedContent.push(newest);
  }
  return deDupedContent;
};

export const rgbToHex = (r: number, g: number, b: number): string => {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

export const proseMirrorJSONToText = (jsonData: JSONContent) => {
  const textValues: string[] = [];

  // Recursive function to traverse the JSON structure
  function extractText(node) {
    if (node.type === "text" && node.text) {
      textValues.push(node.text);
    }

    if (node.content && Array.isArray(node.content)) {
      for (const child of node.content) {
        extractText(child);
      }
    }
  }

  // Start extraction from the "content" array
  if (jsonData.content && Array.isArray(jsonData.content)) {
    for (const contentItem of jsonData.content) {
      extractText(contentItem);
    }
  }

  // Join the extracted text values with a space
  return textValues.join(" ");
};

export const docIsEmpty = (doc: JSONContent): boolean => {
  if (typeof doc === "string") {
    return doc?.length === 0;
  }

  if (Array.isArray(doc)) {
    return doc.every(docIsEmpty);
  }

  if (typeof doc === "object" && doc !== null) {
    if (doc.type === "text") {
      return doc?.text?.length === 0;
    }

    if (doc.content) {
      return docIsEmpty(doc.content);
    }
  }

  return true;
};

export const findNodeByAttribute = (
  doc: Node,
  attrName: string,
  attrValue: any
): ResolvedPos | null => {
  let result = null;
  doc.descendants((node, pos) => {
    if (node.attrs[attrName] === attrValue) {
      result = doc.resolve(pos);
      return false; // Stop the traversal early
    }
    return true; // Continue the traversal
  });
  return result;
};

export const getListItemText = (node: Node) => {
  let html = "";

  node.forEach((childNode) => {
    if (childNode.isText) {
      let textHTML = childNode.text;

      childNode.marks.forEach((mark) => {
        if (mark.type.name === "bold") {
          textHTML = `<strong>${textHTML}</strong>`;
        }
        if (mark.type.name === "italic") {
          textHTML = `<em>${textHTML}</em>`;
        }
        if (mark.type.name === "strike") {
          textHTML = `<s>${textHTML}</s>`;
        }
        if (mark.type.name === "code") {
          textHTML = `<code>${textHTML}</code>`;
        }
        if (mark.type.name === "link") {
          textHTML = `<a href="${mark.attrs.href}" target="_blank">${textHTML}</a>`;
        }
      });

      html += textHTML;
    } else if (childNode.isBlock && childNode.type.name !== "listItem") {
      html += getListItemText(childNode);
    }
  });

  return html;
};

export const findAncestorOfType = (pos: ResolvedPos, type: NodeType) => {
  for (let depth = pos.depth; depth > 0; depth--) {
    let ancestor = pos.node(depth);
    if (ancestor.type === type) {
      return ancestor;
    }
  }
  return null;
};
